import { CalendarIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../../../../ui/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../ui/components/ui/popover';
import { FilterMetaYearResponseDto } from '../../types/response';

interface KpiMonthRangeSelectorComponentProps {
  years: FilterMetaYearResponseDto[];
  currentFromYear: number;
  currentFromMonth: number;
  currentToYear: number;
  currentToMonth: number;
  disabled?: boolean;
  onChange: (range: { from: { month: string; year: string }; to: { month: string; year: string } }) => void;
}

interface MonthPickerProps {
  label: string;
  years: FilterMetaYearResponseDto[];
  pendingYear: number;
  pendingMonth: number | null;
  onYearSelect: (year: number) => void;
  onMonthSelect: (month: number) => void;
  isMonthDisabled?: (month: number, year: number) => boolean;
}

const MonthPicker = ({ label, years, pendingYear, pendingMonth, onYearSelect, onMonthSelect, isMonthDisabled }: MonthPickerProps) => {
  const months = years.find((y) => y.year === pendingYear)?.months ?? [];

  return (
    <div className="space-y-3 flex-1">
      <p className="text-xs font-semibold text-[#12282A]">{label}</p>

      <div className="space-y-1.5">
        <p className="text-xs font-medium text-muted-foreground">Jahr</p>
        <div className="flex flex-wrap gap-1.5">
          {years.map(({ year }) => (
            <Button
              key={year}
              size="sm"
              variant={pendingYear === year ? 'default' : 'outline'}
              className="h-7 px-2.5 text-xs"
              onClick={() => onYearSelect(year)}>
              {year}
            </Button>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <p className="text-xs font-medium text-muted-foreground">Monat</p>
        <div className="grid grid-cols-3 gap-1">
          {months.map(({ month, title }) => {
            const disabled = isMonthDisabled?.(month, pendingYear) ?? false;
            return (
              <Button
                key={month}
                size="sm"
                variant={pendingMonth === month ? 'default' : 'ghost'}
                className="h-7 text-xs"
                disabled={disabled}
                onClick={() => onMonthSelect(month)}>
                {title}
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const KpiMonthRangeSelectorComponent = ({
  years,
  currentFromYear,
  currentFromMonth,
  currentToYear,
  currentToMonth,
  disabled,
  onChange
}: KpiMonthRangeSelectorComponentProps) => {
  const [open, setOpen] = useState(false);

  const [fromYear, setFromYear] = useState<number>(currentFromYear);
  const [fromMonth, setFromMonth] = useState<number | null>(currentFromMonth);
  const [toYear, setToYear] = useState<number>(currentToYear);
  const [toMonth, setToMonth] = useState<number | null>(currentToMonth);

  useEffect(() => {
    setFromYear(currentFromYear);
    setFromMonth(currentFromMonth);
    setToYear(currentToYear);
    setToMonth(currentToMonth);
  }, [currentFromYear, currentFromMonth, currentToYear, currentToMonth]);

  const isToMonthDisabled = (month: number, year: number) => {
    if (fromMonth === null) return false;
    if (year > fromYear) return false;
    if (year < fromYear) return true;
    return month < fromMonth;
  };

  const handleFromYearSelect = (year: number) => {
    setFromYear(year);
    setFromMonth(year === currentFromYear ? currentFromMonth : null);
    if (toMonth !== null) {
      const fromAfterTo = year > toYear || (year === toYear && (fromMonth ?? 0) > (toMonth ?? 0));
      if (fromAfterTo) {
        setToYear(year);
        setToMonth(null);
      }
    }
  };

  const handleFromMonthSelect = (month: number) => {
    setFromMonth(month);
    if (toMonth !== null && (fromYear > toYear || (fromYear === toYear && month > toMonth))) {
      setToMonth(null);
    }
  };

  const handleToYearSelect = (year: number) => {
    setToYear(year);
    setToMonth(year === currentToYear ? currentToMonth : null);
  };

  const canConfirm = fromMonth !== null && toMonth !== null;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onChange({
      from: { month: String(fromMonth), year: String(fromYear) },
      to: { month: String(toMonth), year: String(toYear) }
    });
    setOpen(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setFromYear(currentFromYear);
      setFromMonth(currentFromMonth);
      setToYear(currentToYear);
      setToMonth(currentToMonth);
    }
    setOpen(next);
  };

  const triggerLabel = (() => {
    const fromTitle =
      years.find((y) => y.year === currentFromYear)?.months.find((m) => m.month === currentFromMonth)?.title ?? String(currentFromMonth);
    const toTitle = years.find((y) => y.year === currentToYear)?.months.find((m) => m.month === currentToMonth)?.title ?? String(currentToMonth);
    return `${currentFromYear} ${fromTitle} – ${currentToYear} ${toTitle}`;
  })();

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" disabled={disabled} className="gap-2 text-xs h-8">
          <CalendarIcon className="h-4 w-4 min-w-[20px] aspect-square" />
          {triggerLabel}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[520px] p-4 space-y-4" align="start">
        <div className="flex gap-6">
          <MonthPicker
            label="Von"
            years={years}
            pendingYear={fromYear}
            pendingMonth={fromMonth}
            onYearSelect={handleFromYearSelect}
            onMonthSelect={handleFromMonthSelect}
          />

          <div className="w-px bg-[#E4E7EC]" />

          <MonthPicker
            label="Bis"
            years={years}
            pendingYear={toYear}
            pendingMonth={toMonth}
            onYearSelect={handleToYearSelect}
            onMonthSelect={(month) => setToMonth(month)}
            isMonthDisabled={isToMonthDisabled}
          />
        </div>

        <Button className="w-full" size="sm" disabled={!canConfirm} onClick={handleConfirm}>
          Übernehmen
        </Button>
      </PopoverContent>
    </Popover>
  );
};

export default KpiMonthRangeSelectorComponent;
